import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Row, Button } from "@narmi/design_system";
import { entities } from "byzantine";
import {
  bulkTemplateSchema,
  templatePaymentsSchema,
} from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates";
import { calculateBulkTotals } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import { LimitsForDisplayType } from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import HeaderWithSteps from "../../../HeaderWithSteps";
import Container from "../../../Container";
import LimitsAndDisclosuresDialog from "../../../transfer/ach/LimitsAndDisclosuresDialog";
import styles from "../../../PaymentActivity/CreateBulk/CreateBulk.module.scss";
import BulkTotalsDisplay from "../../../PaymentActivity/CreateBulk/BulkTotalsDisplay";
import bulkTableStyles from "../../../PaymentActivity/CreateBulk/table/BulkPaymentTable.module.scss";
import { TableErrorSummary } from "../../../PaymentActivity/CreateBulk/table";
import { PaymentsTable } from "./table";
import AllowExpediteCard from "./AllowExpediteCard";

const TemplatePaymentsScreen = ({
  limits,
  isEditMode = false,
}: {
  limits: LimitsForDisplayType;
  isEditMode?: boolean;
}) => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clickedReview, setClickedReview] = useState(false);
  const [showLimitsDialog, setShowLimitsDialog] = useState(false);
  const form = entities.bulkTemplates.BulkTemplateForm.useForm();
  const { submitForm, setValidationSchema, setFormikState } = form;
  const { payments } = form.values;
  const {
    collectTotalFormatted,
    sendTotalFormatted,
    netTotalFormattedWithSign,
  } = calculateBulkTotals(payments);

  useEffect(() => {
    // when user lands on this screen, set validation schema to include "payments"
    setValidationSchema((dbblL10n) =>
      bulkTemplateSchema(dbblL10n).concat(templatePaymentsSchema(dbblL10n))
    );
    // clicking "Next" on the details screen counts as a form submit,
    // which makes row errors show immediately.
    // reset the count so they stay hidden until the user clicks "Review" on payments table screen
    setFormikState((prev) => ({ ...prev, submitCount: 0 }));
  }, []);

  const handleClickReview = async () => {
    setClickedReview(true);
    const { success } = await submitForm();
    if (!success) {
      return;
    }
    navigate({ pathname: "../review", search: searchParams.toString() });
  };

  return (
    <div className={styles.paymentsScreenContainer}>
      <Container maxWidth="1340px">
        <HeaderWithSteps
          headerText={l10n.getString(
            isEditMode ? "edit-template-header" : "create-template-header"
          )}
        />

        <TableErrorSummary clickedReview={clickedReview} form={form} />
        <div className="margin--top--xl">
          <PaymentsTable />
        </div>

        <AllowExpediteCard />

        <BulkTotalsDisplay
          collectTotalFormatted={collectTotalFormatted}
          sendTotalFormatted={sendTotalFormatted}
          netTotalFormattedWithSign={netTotalFormattedWithSign}
          hasCollectRows={payments.some((p) => p.transactionType === "collect")}
        />

        <div className={bulkTableStyles.headingContainer}>
          <div className={bulkTableStyles.endRowContainer}>
            <Button
              label={l10n.getString("ach-payment-view-limits-bulk-cta")}
              kind="plain"
              size="s"
              onClick={() => setShowLimitsDialog(true)}
            />
          </div>
        </div>

        <div className="margin--top--xl">
          <Row alignItems="center" justifyContent="end">
            <Row.Item shrink>
              <Button
                kind="negative"
                label={l10n.getString("button-back")}
                onClick={() => {
                  navigate(-1);
                }}
              />
            </Row.Item>
            <Row.Item shrink>
              <Button
                label={l10n.getString("heading-review")}
                onClick={handleClickReview}
              />
            </Row.Item>
          </Row>
        </div>

        <LimitsAndDisclosuresDialog
          isOpen={showLimitsDialog}
          handleClose={() => setShowLimitsDialog(false)}
          limits={limits}
          fromFlow="bulkPayment"
        />
      </Container>
    </div>
  );
};

export default TemplatePaymentsScreen;
